import { useState, useCallback } from 'react';
import { MapPin, Navigation, Factory, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';

// This is a publishable client-side key, restricted via Google Cloud Console (HTTP referrer + API restrictions)
const GOOGLE_MAPS_API_KEY = 'AIzaSyAoiUy8H9vwob5jI_xDkW1puk7QnzR-o-c';
const LIBRARIES: ("places")[] = ["places"];

const factories = [
  {
    id: 1,
    name: 'Sandhu Feeds — Unit 1',
    location: 'Assandh, Karnal, Haryana',
    status: 'Head Office & Manufacturing',
    lat: 29.5008398,
    lng: 76.5586986,
    address: 'Jind Road, Assandh, District Karnal, Haryana 132039',
    mapQuery: 'Sandhu+Feeds,+Assandh,+Karnal,+Haryana',
  },
  {
    id: 2,
    name: 'Sandhu Feeds — Unit 2',
    location: 'Assandh, Karnal, Haryana',
    status: 'Manufacturing',
    lat: 29.5008398,
    lng: 76.5586986,
    address: 'Jind Road, Assandh, District Karnal, Haryana 132039',
    mapQuery: 'Sandhu+Feeds,+Assandh,+Karnal,+Haryana',
  },
  {
    id: 3,
    name: 'Sandhu Feeds — Bihar Plant',
    location: 'Motipur, Bihar',
    status: 'Manufacturing',
    lat: 26.2930491,
    lng: 85.0816835,
    address: 'Motipur, Bihar',
    mapQuery: '26.2930491,85.0816835',
  },
  {
    id: 4,
    name: 'Sandhu Feeds — Assam Plant',
    location: 'Guwahati, Assam',
    status: 'Manufacturing',
    lat: 26.1445,
    lng: 91.7362,
    address: 'Guwahati, Assam',
    mapQuery: '26.1445,91.7362',
  },
];

const mapContainerStyle = { width: '100%', height: '450px' };
const indiaCenter = { lat: 26.5, lng: 82.5 };

const LocationMap = () => {
  const [selectedFactory, setSelectedFactory] = useState<number | null>(null);
  const [activeMarker, setActiveMarker] = useState<number | null>(null);
  const [markerIcon, setMarkerIcon] = useState<google.maps.Icon | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const current = selectedFactory !== null ? factories[selectedFactory] : null;

  const embedSrc = current
    ? `https://www.google.com/maps?q=${current.lat},${current.lng}&z=14&output=embed`
    : '';

  const openDirections = (factory: typeof factories[0]) => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${factory.lat},${factory.lng}`,
      '_blank'
    );
  };

  const onMapLoad = useCallback((map: google.maps.Map) => {
    const bounds = new google.maps.LatLngBounds();
    factories.forEach((f) => bounds.extend({ lat: f.lat, lng: f.lng }));
    map.fitBounds(bounds, 60);

    setMarkerIcon({
      url: '/lovable-uploads/e96b7e90-3233-4052-88c0-b565f9c3ca04.png',
      scaledSize: new google.maps.Size(44, 44),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(22, 44),
    });
  }, []);

  return (
    <div className="space-y-10">
      {/* Section header */}
      <div className="text-center">
        <Badge variant="outline" className="mb-3 text-xs tracking-widest uppercase font-body border-primary/30 text-primary">
          Our Presence
        </Badge>
        <h3 className="text-3xl md:text-4xl font-bold font-display text-foreground">
          Factories Across <span className="text-primary">India</span>
        </h3>
        <p className="text-muted-foreground font-body mt-2 max-w-xl mx-auto">
          Strategically located in Haryana, Bihar &amp; Assam to serve poultry farmers nationwide.
        </p>
      </div>

      {/* Factory selector pills */}
      <div className="flex flex-wrap justify-center gap-2">
        <button
          onClick={() => setSelectedFactory(null)}
          className={`
            px-4 py-2 rounded-full text-sm font-body font-medium transition-all duration-300
            ${selectedFactory === null
              ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25'
              : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'}
          `}
        >
          <MapPin className="inline-block h-3.5 w-3.5 mr-1.5 -mt-0.5" />
          All
        </button>
        {factories.map((f, i) => (
          <button
            key={f.id}
            onClick={() => setSelectedFactory(i)}
            className={`
              px-4 py-2 rounded-full text-sm font-body font-medium transition-all duration-300
              ${i === selectedFactory
                ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25'
                : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'}
            `}
          >
            <MapPin className="inline-block h-3.5 w-3.5 mr-1.5 -mt-0.5" />
            {f.location.split(',')[0]}
          </button>
        ))}
      </div>

      {/* Map */}
      <div className="relative rounded-2xl overflow-hidden border border-border shadow-xl">
        {selectedFactory === null ? (
          isLoaded ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={indiaCenter}
              zoom={5}
              onLoad={onMapLoad}
              options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
              }}
            >
              {factories.map((f) => (
                <Marker
                  key={f.id}
                  position={{ lat: f.lat, lng: f.lng }}
                  title={f.name}
                  icon={markerIcon || undefined}
                  onClick={() => setActiveMarker(f.id)}
                >
                  {activeMarker === f.id && (
                    <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                      <div style={{ padding: '4px', maxWidth: '200px' }}>
                        <strong style={{ fontSize: '13px' }}>{f.name}</strong>
                        <br />
                        <span style={{ fontSize: '11px', color: '#666' }}>{f.address}</span>
                        <br />
                        <em style={{ fontSize: '11px' }}>{f.status}</em>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              ))}
            </GoogleMap>
          ) : (
            <div className="flex items-center justify-center h-[450px] bg-muted">
              <p className="text-muted-foreground font-body">Loading map…</p>
            </div>
          )
        ) : (
          <iframe
            title="Sandhu Feeds Factory Location"
            src={embedSrc}
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full"
          />
        )}
        {/* Overlay badge */}
        <div className="absolute top-4 left-4 z-[1000] bg-background/90 backdrop-blur-sm rounded-lg px-4 py-2 border border-border shadow-md">
          <p className="text-sm font-semibold text-foreground font-display">
            {current ? current.name : 'All Factories'}
          </p>
          <p className="text-xs text-muted-foreground font-body">
            {current ? current.address : 'Haryana, Bihar & Assam'}
          </p>
        </div>
      </div>

      {/* Factory cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {factories.map((f, i) => (
          <div
            key={f.id}
            onClick={() => setSelectedFactory(i)}
            className={`
              group relative flex flex-col rounded-xl border p-5 cursor-pointer transition-all duration-300
              ${i === selectedFactory
                ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10 ring-1 ring-primary/20'
                : 'border-border bg-card hover:border-primary/40 hover:shadow-md'}
            `}
          >
            <div className={`
              w-10 h-10 rounded-lg flex items-center justify-center mb-4 text-sm font-bold font-display
              ${i === selectedFactory
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'}
              transition-colors duration-300
            `}>
              {String(f.id).padStart(2, '0')}
            </div>

            <h4 className="font-semibold text-foreground font-display text-sm mb-1">{f.name}</h4>
            <p className="text-xs text-muted-foreground font-body mb-3 min-h-[2.5rem]">{f.address}</p>

            <div className="mb-4">
              <Badge
                variant={f.status === 'Head Office & Manufacturing' ? 'default' : 'secondary'}
                className="text-[10px]"
              >
                <Factory className="h-3 w-3 mr-1" />
                {f.status}
              </Badge>
            </div>

            {f.id !== 4 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openDirections(f);
                }}
                className="flex items-center text-xs font-medium text-primary hover:underline font-body mt-auto"
              >
                <Navigation className="h-3 w-3 mr-1" />
                Get Directions
                <ExternalLink className="h-3 w-3 ml-1 opacity-50" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LocationMap;
